import { Box, FormControl } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import img0 from 'components/@welcome/OnboardingScene/images/onboarding-mock-0.png';
import deferRender from 'defer-render-hoc';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { Link, useLocation, useNavigate } from 'react-router';
import authActions from 'store/app/auth/action';
import companiesActions from 'store/app/entities/companies/action';
import employeesActions from 'store/app/entities/employees/action';
import authCompany from 'store/selectors/authCompany';
import authEmployee from 'store/selectors/authEmployee';
import useActions from 'utils/useActions';
import IAcceptTermsButton from './IAcceptTermsButton';

const paperSx = {
  width: '580px',
  minHeight: '720px',
  maxWidth: '100%',
  maxHeight: '720px',
  margin: '1em 0',
  display: 'flex',
  flexDirection: 'column',
  boxShadow:
    '0px 11px 15px -7px rgba(0,0,0,0.05), ' +
    '0px 24px 38px 3px rgba(0,0,0,0.02), ' +
    '0px 9px 46px 8px rgba(0,0,0,0.01)',
  flexGrow: 1,
  overflow: 'hidden',
};

const LegalScene = () => {
  const [auth, companies, employees] = useActions([
    authActions,
    companiesActions,
    employeesActions,
  ]);
  const actions = { auth, companies, employees };
  const company = useSelector(authCompany);
  const employee = useSelector(authEmployee);
  const location = useLocation();
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(location.state);
  const [firstName, setFirstName] = useState(employee?.firstName);
  const [surName, setSurName] = useState(employee?.surName);

  const onCheckboxChange = () => {
    setAccepted(a => !a);
  };

  const onButtonClick = () => {
    if (!accepted) {
      return;
    }

    if (employee && (!employee.firstName || !employee.surName)) {
      actions.employees.update(employee._id, {
        firstName,
        surName,
      });
    }
    actions.companies.acceptTerms(company._id);

    navigate('/home/messages');
  };

  const changeName = func => event => {
    func(event.target.value);
  };

  const canSubmit = accepted && firstName && surName;

  const { name, logo, onBoardingImage } = company;
  return (
    <Box
      component="section"
      sx={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <Paper sx={paperSx} elevation={24}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '1.75em',
          }}
        >
          <Box
            sx={{
              backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.0) 70%, rgba(0, 0, 0, 0.4)), url(${img0})`,
              flexGrow: 1,
              minHeight: '200px',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              width: '100%',
              ...(onBoardingImage && {
                backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.0) 70%, rgba(0, 0, 0, 0.4)), url(${onBoardingImage})`,
              }),
            }}
          >
            {logo && (
              <Box
                component="img"
                src={logo}
                alt={name}
                sx={{
                  maxHeight: '60px',
                  maxWidth: '200px',
                  textAlign: 'center',
                  marginBottom: '20px',
                }}
              />
            )}
          </Box>
        </Box>
        <Box
          component="section"
          sx={{
            padding: '0 3em',
            marginTop: '24px',
          }}
        >
          <Typography sx={{ margin: '1em 0 0.5em 0' }} gutterBottom variant="h5">
            {employee && (!employee.firstName || !employee.surName)
              ? I18n.t('CompanyTerms.Whats your name')
              : I18n.t('CompanyTerms.title', { name })}
          </Typography>
          {employee && (!employee.firstName || !employee.surName) && (
            <div>
              <FormControl
                fullWidth
                sx={{
                  alignItems: 'baseline !important',
                  marginBottom: '10px',
                }}
              >
                <TextField
                  name="firstName"
                  value={firstName}
                  type="text"
                  onChange={changeName(setFirstName)}
                  placeholder={`${I18n.t('CompanyTerms.First name')}*`}
                  fullWidth
                  required
                />
              </FormControl>
              <FormControl
                fullWidth
                sx={{
                  alignItems: 'baseline !important',
                  marginBottom: '10px',
                }}
              >
                <TextField
                  name="firstName"
                  value={surName}
                  type="text"
                  onChange={changeName(setSurName)}
                  placeholder={`${I18n.t('CompanyTerms.Last name')}*`}
                  fullWidth
                  required
                />
              </FormControl>
            </div>
          )}
          <FormControlLabel
            sx={{
              alignItems: 'baseline !important',
              marginBottom: '10px',
            }}
            control={
              <Checkbox
                sx={{
                  position: 'relative',
                  top: '0.35rem',
                }}
                name="terms"
                checked={!!accepted}
                onChange={onCheckboxChange}
                value="terms"
              />
            }
            label={
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 'normal',
                  textAlign: 'left',
                  '& a': {
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  },
                }}
              >
                {I18n.t('CompanyTerms.Accept_Terms')}
                <Link to="/welcome/legal/terms">{I18n.t('CompanyTerms.Terms_And_Conditions')}</Link>
                {I18n.t('CompanyTerms.and')}
                <Link to="/welcome/legal/privacy">
                  {I18n.t('CompanyTerms.Privacy_Policy', { name })}
                </Link>
              </Typography>
            }
          />
        </Box>
        <Box
          sx={{
            marginTop: '2em',
            paddingBottom: '2em',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IAcceptTermsButton disabled={!canSubmit} onClick={onButtonClick} />
        </Box>
      </Paper>
    </Box>
  );
};
export default deferRender(LegalScene);
